import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Laptop, Loader2, RefreshCw, ShieldAlert, ShieldCheck, ChevronDown, ChevronUp, LifeBuoy,
} from 'lucide-react';
import api from '../lib/api';

const UpdateList = ({ title, items, count }) => {
  if (!count) return null;
  return (
    <div className="mt-2">
      <div className="text-xs font-semibold text-slate-700">{title} ({count})</div>
      <ul className="mt-1 space-y-0.5">
        {items.map((u, i) => (
          <li key={`${u.name}-${i}`} className="text-xs text-slate-600 flex items-center gap-2">
            <span className="truncate">{u.name}{u.kb ? ` (${u.kb})` : ''}</span>
            {u.sla_status === 'Overdue' && (
              <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0">Overdue</Badge>
            )}
          </li>
        ))}
        {count > items.length && (
          <li className="text-xs text-slate-400">and {count - items.length} more</li>
        )}
      </ul>
    </div>
  );
};

const DeviceRow = ({ device, onRequestHelp }) => {
  const [open, setOpen] = useState(device.needs_attention);
  const mu = device.missing_updates || {};
  const critical = mu.critical_count || 0;
  const other = mu.other_count || 0;

  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid="portal-device-row">
      <button type="button" className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3 min-w-0">
          {device.needs_attention
            ? <ShieldAlert className="h-5 w-5 text-orange-500 shrink-0" />
            : <ShieldCheck className="h-5 w-5 text-green-600 shrink-0" />}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{device.name}</div>
            <div className="text-xs text-slate-500 truncate">
              {device.os}
              {device.online === true && ' · Online'}
              {device.online === false && ' · Offline'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {critical > 0 && <Badge className="bg-red-100 text-red-700">{critical} critical</Badge>}
          {other > 0 && <Badge className="bg-slate-100 text-slate-600">{other} other</Badge>}
          {device.reboot_required && <Badge className="bg-orange-100 text-orange-700">Restart needed</Badge>}
          {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="mt-3 pl-8">
          <ul className="space-y-1">
            {(device.advice || []).map((a, i) => (
              <li key={i} className="text-sm text-slate-700">{a}</li>
            ))}
          </ul>
          <UpdateList title="Critical" items={mu.critical || []} count={critical} />
          <UpdateList title="Other" items={mu.other || []} count={other} />
          {!mu.details_available && (critical > 0 || other > 0) && (
            <p className="text-xs text-slate-400 mt-2">Update details are temporarily unavailable.</p>
          )}
          {device.suggest_ticket && (
            <Button size="sm" variant="outline" className="mt-3 gap-1.5 h-8 text-xs" onClick={() => onRequestHelp(device)}>
              <LifeBuoy className="h-3.5 w-3.5" /> Ask IT for help
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const MyDevicesCard = ({ authMethod, onRequestHelp }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.portalGetMyDevices();
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load your devices right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authMethod === 'entra') load();
  }, [authMethod]);

  return (
    <Card className="border-slate-200" data-testid="portal-my-devices">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Laptop className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-900">My Devices</h2>
          </div>
          {authMethod === 'entra' && (
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Refresh
            </Button>
          )}
        </div>

        {authMethod !== 'entra' ? (
          <p className="text-sm text-slate-500">
            Sign out and sign back in with your Microsoft work account to see update status for your computers.
          </p>
        ) : loading && !data ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : data && data.devices?.length ? (
          <div className="space-y-2">
            {data.devices.map((d) => (
              <DeviceRow key={d.endpoint_id} device={d} onRequestHelp={onRequestHelp} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">{data?.message || 'No computers found for your account.'}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default MyDevicesCard;
